/**
 * SportsnewContext — state + socket management for the /sportsnew/* API.
 *
 * Socket contract (per sport_socket_guide.md):
 *   Namespace : /ws/sports
 *   Emit      : subscribe:sportsnew:list  | unsubscribe:sportsnew:list  | heartbeat:sportsnew:list
 *               subscribe:sportsnew:fixture | unsubscribe:sportsnew:fixture
 *               subscribe:sportsnew:selected | unsubscribe:sportsnew:selected
 *               ack:sportsnew:patch
 *   Listen    : sportsnew:list:patch | sportsnew:fixture:patch
 *               sportsnew:selected:patch | sportsnew:bet:result
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import io from 'socket.io-client';
import useServiceContextHook from '../_hooks/service.context.hook';
import { getStoredAuthToken } from '../_helpers/authTokenStorage.helper';
import { SportsBetCartContext } from './SportsBetCartContext';
import { fxId, applyListPatch, diffMainOdds, resolveMainOddsUpdatesFromListPatch, applyMainOddsToDetailMarkets, applyOddPatchToFixtureList, applyFixturePatchToFixture, applyFixturePatchToFixtureList, isOddLocked, applyFixtureDetailPatch } from './_helpers/sportsnewListPatch';
import { reconcileDetailMarketsFromRestSnapshot } from './_helpers/sportsnewDetailRestReconcile';
import {
  normalizeSelectedRecoveryIds,
  recoverSelectedOddsCoalesced,
  selectedPatchFreshness,
} from './_helpers/sportsnewSelectedRecovery';
import {
  resolveOperatorVisibilityPatch,
  stripOperatorVisibilityRemovals,
  upsertOperatorResumedFixtures,
} from './_helpers/sportsnewOperatorVisibility';
import {
  invalidateSportsnewFixtureDetailRequest,
  isSportsnewFixtureDetailGenerationCurrent,
} from './_helpers/sportsnewFixtureDetailLifecycle';
import {
  isSportsnewFixturePatchForActiveDetail,
  sportsnewActiveFixtureMode,
  sportsnewFixtureMetadataDelta,
  sportsnewFixturePatchTargetId,
} from './_helpers/sportsnewFixturePatchEnvelope';
import { createEvictionScheduler } from './_helpers/evictionScheduler';
import {
  isEntityLocked,
  isMarketFullyLocked,
  marketOdds,
  isDisplayOnlyPairSlot,
  findReplacementOdd,
  resolveReplacementHideDelayMs,
  remainingHideDelayMs,
} from './_helpers/sportsnewDisplayLifecycle';
import {
  markPerf,
  markListLoaded,
  markDetailClick,
  measureDetailHeader,
  measureFirstMarket,
  measureFullDetailLoaded,
  clearDetailClickMark,
  markWsPatchReceived,
  measureWsPatchToRender,
} from './_helpers/sportsnewPerf';
import {
  SPORTSNEW_LIST_STATUS,
  classifySportsnewListResponse,
  shouldRetrySportsnewList,
  sportsnewListRequestKey,
  sportsnewListRetryDelayMs,
} from './_helpers/sportsnewListRequestState';
import { cancelSportsnewListFlights } from './_helpers/sportsnewListFlight';

export { fxId, applyListPatch, diffMainOdds, resolveMainOddsUpdatesFromListPatch, applyMainOddsToDetailMarkets, applyOddPatchToFixtureList, applyFixturePatchToFixtureList } from './_helpers/sportsnewListPatch';

/* ── Supported modes and sports ── */
export const SPORTSNEW_MODES = ['prematch', 'live', 'all'];
export const SPORTSNEW_SPORTS = ['soccer', 'baseball', 'basketball', 'volleyball', 'ice-hockey', 'esports'];
export const SPORTSNEW_DISPLAY_SCOPES = ['overseas', 'domestic', 'live', 'esports'];
const SPORTSNEW_WS_DEBUG = ['1', 'true'].includes(
  String(process.env.REACT_APP_SPORTSNEW_WS_DEBUG || '').toLowerCase(),
);

const normalizeSportsnewComboBonus = value => {
  const data = value?.data ?? value;
  const tiers = Array.isArray(data)
    ? data
    : data?.tiers || data?.comboBonusTiers || data?.combo_bonus_tiers || data?.comboBonus?.tiers || [];
  if (!Array.isArray(tiers)) return [];
  return tiers
    .map(tier => {
      const fold = Number(tier.fold ?? tier.min_legs ?? tier.minLegs);
      const multiplier = Number(tier.value ?? tier.multiplier);
      const rate = Number(tier.ratePercent ?? tier.rate_percent);
      const resolvedValue = Number.isFinite(multiplier) && multiplier >= 1
        ? multiplier
        : Number((1 + (Number.isFinite(rate) ? rate : 0) / 100).toFixed(4));
      if (!Number.isFinite(fold) || fold < 2 || !Number.isFinite(resolvedValue) || resolvedValue <= 1) {
        return null;
      }
      return { ...tier, fold, value: resolvedValue };
    })
    .filter(Boolean)
    .sort((a, b) => a.fold - b.fold);
};

const sportFallbackOrder = (slug) => {
  const index = SPORTSNEW_SPORTS.indexOf(slug);
  return index >= 0 ? index + 1 : SPORTSNEW_SPORTS.length + 1;
};

const categorySortOrder = (category) => {
  const value = Number(category?.sortOrder ?? category?.sort_order);
  return Number.isFinite(value) && value > 0 ? value : sportFallbackOrder(category?.slug);
};

const normalizeFixtureRoomPatch = (patch = {}) => {
  if (!Array.isArray(patch?.marketSnapshot)) return patch;
  return {
    ...patch,
    markets: patch.marketSnapshot,
  };
};

const recoverFixtureSnapshotPayload = (payload) => {
  const snapshot = payload?.snapshot && typeof payload.snapshot === 'object'
    ? payload.snapshot
    : null;
  const isRecoveryEnvelope = payload && (
    Object.prototype.hasOwnProperty.call(payload, 'room') ||
    Object.prototype.hasOwnProperty.call(payload, 'gap') ||
    Object.prototype.hasOwnProperty.call(payload, 'snapshot')
  );
  const directFixture = payload?.fixture
    ?? (!isRecoveryEnvelope && (payload?.fixtureId != null || payload?.id != null) ? payload : null);
  let markets = null;
  if (Array.isArray(snapshot?.markets)) markets = snapshot.markets;
  else if (Array.isArray(payload?.markets)) markets = payload.markets;
  return {
    fixture: snapshot?.fixture ?? directFixture,
    markets,
  };
};

const listSubscriptionFields = [
  'mode',
  'scope',
  'sport',
  'filterHash',
  'leagueId',
  'gameTitleId',
  'tournamentId',
  'status',
  'featured',
  'page',
  'limit',
];

const subscriptionValue = (value) => value == null ? '' : String(value);

const sportSlugOf = (sport) => (
  sport ? String(typeof sport === 'object' ? sport.slug : sport).trim() : ''
);

const isListFilterModeScopeReady = ({ mode, scope }) => {
  const normalizedMode = String(mode || '').trim().toLowerCase();
  const normalizedScope = String(scope || '').trim().toLowerCase();
  if (normalizedMode === 'live') return normalizedScope === 'live';
  return normalizedScope !== 'live';
};

const listSubscriptionChanged = (previous, next) => (
  !previous || listSubscriptionFields.some((field) => subscriptionValue(previous?.[field]) !== subscriptionValue(next?.[field]))
);

const subscriptionBoolean = (value) => value === true || value === 'true' || value === '1';

const normalizedListSubscription = ({ echoed = {}, params = {}, hash, pageFallback, limitFallback }) => ({
  mode: echoed.mode ?? params.mode,
  scope: echoed.scope ?? params.scope,
  sport: echoed.sport ?? params.sport ?? 'all',
  filterHash: hash,
  leagueId: echoed.leagueId ?? echoed.league_id ?? params.league_id ?? null,
  gameTitleId: echoed.gameTitleId ?? echoed.game_title_id ?? params.game_title_id ?? null,
  tournamentId: echoed.tournamentId ?? echoed.tournament_id ?? params.tournament_id ?? null,
  status: echoed.status ?? params.status ?? null,
  featured: subscriptionBoolean(echoed.featured ?? params.featured),
  page: Number(echoed.page ?? params.page ?? pageFallback ?? 1) || 1,
  limit: Number(echoed.limit ?? params.limit ?? limitFallback ?? 30) || 30,
});

const normalizeSportsnewCategories = (rows) => (
  Array.isArray(rows) ? rows : []
).map((category, index) => ({
  ...category,
  id: category.id ?? category.sportId ?? category.sport_id ?? category.slug ?? index,
  sportId: category.sportId ?? category.sport_id ?? category.id ?? null,
  sortOrder: categorySortOrder(category),
})).sort((a, b) => (
  categorySortOrder(a) - categorySortOrder(b)
  || sportFallbackOrder(a.slug) - sportFallbackOrder(b.slug)
  || String(a.slug || '').localeCompare(String(b.slug || ''))
));

export const SportsnewContext = createContext();

export const SportsnewProvider = ({ children }) => {
  const { services, session } = useServiceContextHook();
  const SportsnewService = services?.SportsnewService;
  const { selections, updateOddsInCart, isSelectionInAnyCart } = useContext(SportsBetCartContext);

  /* ── Mode / sport filter ── */
  const [mode, setMode] = useState('prematch'); // prematch | live | all
  const [displayScope, setDisplayScope] = useState('overseas'); // overseas | domestic | live | esports
  const [selectedSport, setSelectedSport] = useState(null); // null = all sports
  const [selectedLeagueId, setSelectedLeagueId] = useState(null);
  const [selectedGameTitleId, setSelectedGameTitleId] = useState(null);
  const [selectedTournamentId, setSelectedTournamentId] = useState(null);

  /* ── Data ── */
  const [categories, setCategories] = useState([]);
  const [leagues, setLeagues] = useState([]);
  // leagueId -> country display name, cross-referenced from the navigation
  // tree (see the effect below) — fixture list rows only carry a flat
  // leagueId/leagueName, never a nested country/flag object, so this is the
  // only reliable source for "which country is this league from" (used to
  // prefix league headers with a flag + country name across all 4 pages).
  const [leagueCountryMap, setLeagueCountryMap] = useState({});
  const [fixtures, setFixtures] = useState([]);
  const [featuredFixtures, setFeaturedFixtures] = useState([]);
  const [detailFixture, setDetailFixture] = useState(null);
  const [detailMarkets, setDetailMarkets] = useState([]);
  const [counts, setCounts] = useState(null);
  const [liveCount, setLiveCount] = useState(0);
  const [displaySettings, setDisplaySettings] = useState(null);
  const [comboBonus, setComboBonus] = useState(null);
  const [comboBuckets, setComboBuckets] = useState({});
  const [bettingLimits] = useState(null);
  const [bettingPolicy] = useState(null);

  /* ── Loading / error ── */
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState(null);
  const [listStatus, setListStatus] = useState(SPORTSNEW_LIST_STATUS.IDLE);

  /* ── Pagination ── */
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize] = useState(30);

  // Reset to page 1 whenever the filter dimensions change so we never show stale pages
  useEffect(() => {
    setPage(1);
  }, [mode, displayScope, selectedSport, selectedLeagueId, selectedGameTitleId, selectedTournamentId]);

  /* ── Socket ── */
  const socketRef = useRef(null);
  const [socketConnected, setSocketConnected] = useState(false);

  /* ── Sequence tracking for patch ack / gap detection ── */
  const listSequenceRef = useRef(null);
  const fixtureSequenceRef = useRef(null);
  const selectedSequenceRef = useRef(null);
  const selectedWsFreshnessRef = useRef(new Map());
  const selectedRecoveryInFlightRef = useRef(new Map());

  /* ── Recovery cooldown: prevent calling recovery more than once per 10s ── */
  const listRecoveryInFlightRef = useRef(false);
  const listRecoveryLastRef = useRef(0);

  /* ── filterHash from the last fixture-list API response ── */
  const filterHashRef = useRef(null);
  const listSubscriptionRef = useRef(null); // { mode, scope, sport, filterHash }

  /* Per-key single-flight and active-request identity for fixture lists. */
  const fixturesRequestSeqRef = useRef(0);
  const activeListRequestRef = useRef(null);
  const listRequestsInFlightRef = useRef(new Map());
  const listLastGoodByKeyRef = useRef(new Map());

  const listFilterKey = useMemo(() => sportsnewListRequestKey({
    mode,
    scope: displayScope,
    sport: sportSlugOf(selectedSport) || 'all',
    league_id: selectedLeagueId,
    game_title_id: selectedGameTitleId,
    tournament_id: selectedTournamentId,
    page,
    limit: pageSize,
  }), [mode, displayScope, selectedSport, selectedLeagueId, selectedGameTitleId, selectedTournamentId, page, pageSize]);

  const cancelListFlights = useCallback(() => {
    cancelSportsnewListFlights(listRequestsInFlightRef.current);
    activeListRequestRef.current = null;
  }, []);

  useEffect(() => {
    if (!SportsnewService) return;
    cancelListFlights();
    fixturesRequestSeqRef.current += 1;

    const snapshot = listLastGoodByKeyRef.current.get(listFilterKey);
    setFixtures(snapshot?.fixtures ?? []);
    setTotalPages(snapshot?.totalPages ?? 1);
    setError(null);

    const ready = isListFilterModeScopeReady({ mode, scope: displayScope });
    setLoading(ready);
    setListStatus(ready ? SPORTSNEW_LIST_STATUS.LOADING : SPORTSNEW_LIST_STATUS.IDLE);

    const previousSubscription = listSubscriptionRef.current;
    if (previousSubscription && socketRef.current?.connected) {
      socketRef.current.emit('unsubscribe:sportsnew:list', previousSubscription);
    }
    listSubscriptionRef.current = null;
    filterHashRef.current = null;
    listSequenceRef.current = null;
  }, [SportsnewService, listFilterKey, mode, displayScope, cancelListFlights]);

  useEffect(() => () => cancelListFlights(), [cancelListFlights]);

  /* ── Heartbeat interval ref ── */
  const heartbeatRef = useRef(null);

  /* ── Current detail fixture id ── */
  const [detailFixtureId, setDetailFixtureId] = useState(null);
  const detailFixtureIdRef = useRef(null);
  /* tracks the latest requested id to discard stale async responses */
  const latestDetailRequestRef = useRef(null);
  /* Invalidates every async detail source, including WS recovery promises.
     Fixture id alone is insufficient when the same fixture is closed and
     reopened before an older recovery response resolves. */
  const detailLifecycleGenerationRef = useRef(0);
  /* ── Detail cache, keyed by `${fixtureId}:${mode}` — lets re-opening a
     recently-viewed match paint instantly (shell + markets) from cache while
     a background revalidate still refreshes it, instead of a full loading
     round trip every time. Never pruned by size/TTL here — it's bounded
     naturally by how many distinct fixtures a user visits in one session. ── */
  const detailCacheRef = useRef(new Map());
  /* ── Aborts the in-flight detail request the moment a newer one starts —
     the existing latestDetailRequestRef staleness check already discards a
     late response's DATA, but doesn't stop the browser from downloading it;
     this actually cancels the underlying HTTP request. ── */
  const detailAbortControllerRef = useRef(null);
  /* ── In-progress chunked market render (see renderMarketsProgressively) —
     cancelled whenever a newer fixture is selected before it finishes. ── */
  const marketChunkRafRef = useRef(null);

  /* ── Odds flash for UI animation ── */
  const [oddsFlash, setOddsFlash] = useState({}); // { [oddStateId]: 'up'|'down' }

  /* ── Display lifecycle: soft-deleted (locked-then-evicted) odds/markets/
     fixtures. The underlying canonical data (fixtures/detailMarkets state)
     is NEVER purged here — only these id sets say "don't render this right
     now". A WS patch that reopens an id removes it from the relevant set
     instantly, at any point, even mid-countdown. See evictionScheduler.js /
     sportsnewDisplayLifecycle.js for the timer + decision logic. ── */
  const [hiddenOddIds, setHiddenOddIds] = useState(() => new Set());
  const [hiddenMarketIds, setHiddenMarketIds] = useState(() => new Set());
  const [hiddenFixtureIds, setHiddenFixtureIds] = useState(() => new Set());
  const [operatorSuspendedFixtureIds, setOperatorSuspendedFixtureIds] = useState(() => new Set());
  const hiddenOddIdsRef = useRef(hiddenOddIds);
  const hiddenMarketIdsRef = useRef(hiddenMarketIds);
  const hiddenFixtureIdsRef = useRef(hiddenFixtureIds);
  useEffect(() => { hiddenOddIdsRef.current = hiddenOddIds; }, [hiddenOddIds]);
  useEffect(() => { hiddenMarketIdsRef.current = hiddenMarketIds; }, [hiddenMarketIds]);
  useEffect(() => { hiddenFixtureIdsRef.current = hiddenFixtureIds; }, [hiddenFixtureIds]);

  const updateOperatorSuspendedFixtureIds = useCallback((fixtureIds, suspended) => {
    if (!Array.isArray(fixtureIds) || fixtureIds.length === 0) return;
    setOperatorSuspendedFixtureIds((prev) => {
      const next = new Set(prev);
      fixtureIds.forEach((id) => {
        const normalized = String(id);
        if (suspended) next.add(normalized);
        else next.delete(normalized);
      });
      return next;
    });
  }, []);

  // One eviction-timer namespace per entity level — never recreated across
  // renders, cancelled in full on unmount so no timer fires after teardown.
  const oddEvictionSchedulerRef = useRef(null);
  const marketEvictionSchedulerRef = useRef(null);
  const fixtureEvictionSchedulerRef = useRef(null);
  if (!oddEvictionSchedulerRef.current) oddEvictionSchedulerRef.current = createEvictionScheduler();
  if (!marketEvictionSchedulerRef.current) marketEvictionSchedulerRef.current = createEvictionScheduler();
  if (!fixtureEvictionSchedulerRef.current) fixtureEvictionSchedulerRef.current = createEvictionScheduler();
  useEffect(() => () => {
    oddEvictionSchedulerRef.current.cancelAll();
    marketEvictionSchedulerRef.current.cancelAll();
    fixtureEvictionSchedulerRef.current.cancelAll();
  }, []);

  /* ── Stable refs so socket callbacks can read current state without stale closures ── */
  const fixturesRef      = useRef([]);
  const detailMarketsRef = useRef([]);
  const selectedSportRef = useRef(selectedSport);
  const selectedLeagueIdRef = useRef(null);
  const selectedGameTitleIdRef = useRef(null);
  const selectedTournamentIdRef = useRef(null);
  const selectionsRef = useRef([]);
  const modeRef = useRef(mode);
  const displayScopeRef = useRef(displayScope);
  const pageRef = useRef(page);
  const pageSizeRef = useRef(pageSize);
  useEffect(() => { fixturesRef.current = fixtures; },           [fixtures]);
  useEffect(() => { detailMarketsRef.current = detailMarkets; }, [detailMarkets]);
  useEffect(() => { detailFixtureIdRef.current = detailFixtureId; }, [detailFixtureId]);
  useEffect(() => { selectedSportRef.current = selectedSport; }, [selectedSport]);
  // Read via ref (not a handleListPatch dependency) so changing the league filter
  // doesn't recreate the callback and force a full socket reconnect.
  useEffect(() => { selectedLeagueIdRef.current = selectedLeagueId; }, [selectedLeagueId]);
  useEffect(() => { selectedGameTitleIdRef.current = selectedGameTitleId; }, [selectedGameTitleId]);
  useEffect(() => { selectedTournamentIdRef.current = selectedTournamentId; }, [selectedTournamentId]);
  // Same reasoning — handleSelectedPatch needs the current cart to compute a flash
  // direction when the backend omits previousOdds, without reconnecting on every
  // cart change.
  useEffect(() => { selectionsRef.current = selections; }, [selections]);
  // Same reasoning — handleListPatch needs to know the CURRENT mode to reject
  // stale patches from a room we've since left (see the guard inside it).
  useEffect(() => { modeRef.current = mode; }, [mode]);
  useEffect(() => { displayScopeRef.current = displayScope; }, [displayScope]);
  // Same reasoning — handleListPatch's recovery call needs the CURRENT
  // page/limit. Previously `page` was a direct useCallback dependency of
  // handleListPatch, which itself is a dependency of the socket-connection
  // effect — so every pagination click tore down and reopened the ENTIRE
  // WebSocket connection (all rooms unsubscribed and re-subscribed from
  // scratch), which is exactly the "connection keeps dropping" symptom.
  useEffect(() => { pageRef.current = page; }, [page]);
  useEffect(() => { pageSizeRef.current = pageSize; }, [pageSize]);

  const reconcileRestDetailMarkets = useCallback((markets, fixture = null) => (
    reconcileDetailMarketsFromRestSnapshot(detailMarketsRef.current, markets, {
      fixture,
      hiddenOddIds: hiddenOddIdsRef.current,
      hiddenMarketIds: hiddenMarketIdsRef.current,
    })
  ), []);

  /* ─────────────────────────────────────────────────────
     DISPLAY LIFECYCLE — selection/market lock → grace period → evict.
     Runs on every detailMarkets change, regardless of source (initial REST
     load, list-patch mainOdds sync, fixture-patch, selected-patch) — one
     place implementing the whole spec instead of duplicating it per patch
     handler. Compares against the PREVIOUS snapshot so a fresh transition
     into "locked" starts a countdown, a still-locked market on repeat ticks
     doesn't restart it, and any reopen cancels it immediately.
  ───────────────────────────────────────────────────── */
  const prevDetailMarketsForLifecycleRef = useRef([]);
  useEffect(() => {
    const prevMarkets = prevDetailMarketsForLifecycleRef.current;
    const mode = modeRef.current;
    const prevOddsById = new Map();
    prevMarkets.forEach((m) => marketOdds(m).forEach((o) => {
      if (o.oddStateId != null) prevOddsById.set(o.oddStateId, o);
    }));

    const toUnhideOdds = [];
    const toUnhideMarkets = [];

    detailMarkets.forEach((market) => {
      const marketId = market.marketStateId ?? market.id;
      let anyActiveChild = false;

      marketOdds(market).forEach((odd) => {
        const oddId = odd.oddStateId;
        if (oddId == null) return;
        const schedulerId = `odd:${oddId}`;
        const locked = isEntityLocked(odd);

        if (!locked) {
          anyActiveChild = true;
          oddEvictionSchedulerRef.current.cancel(schedulerId);
          if (hiddenOddIdsRef.current.has(oddId)) toUnhideOdds.push(oddId);
          return;
        }

        const prevOdd = prevOddsById.get(oddId);
        const wasLocked = prevOdd ? isEntityLocked(prevOdd) : false;
        const replacement = findReplacementOdd(odd, market);
        const wasPending = oddEvictionSchedulerRef.current.isPending(schedulerId);

        if (!wasLocked || !wasPending) {
          // Fresh transition into locked (or an untracked lock, e.g. right
          // after a reconnect) — start the countdown, honoring how long it
          // may already have been locked (lockedSince) so a recovered
          // session doesn't grant a full fresh grace period for free.
          const delay = replacement
            ? resolveReplacementHideDelayMs(odd, mode)
            : remainingHideDelayMs(odd, mode);
          oddEvictionSchedulerRef.current.schedule(schedulerId, delay, () => {
            setHiddenOddIds((prev) => new Set(prev).add(oddId));
          });
        } else if (replacement) {
          // Was already counting down on the standard delay, but a
          // replacement line just appeared — shorten it.
          oddEvictionSchedulerRef.current.schedule(schedulerId, resolveReplacementHideDelayMs(odd, mode), () => {
            setHiddenOddIds((prev) => new Set(prev).add(oddId));
          });
        }
        // else: still locked, already counting down, no new replacement — leave the timer alone.
      });

      const marketSchedulerId = `market:${marketId}`;
      if (anyActiveChild) {
        marketEvictionSchedulerRef.current.cancel(marketSchedulerId);
        if (hiddenMarketIdsRef.current.has(marketId)) toUnhideMarkets.push(marketId);
      } else if (isMarketFullyLocked(market) && !marketEvictionSchedulerRef.current.isPending(marketSchedulerId)) {
        marketEvictionSchedulerRef.current.schedule(marketSchedulerId, remainingHideDelayMs(market, mode), () => {
          setHiddenMarketIds((prev) => new Set(prev).add(marketId));
        });
      }
    });

    if (toUnhideOdds.length > 0) {
      setHiddenOddIds((prev) => {
        const next = new Set(prev);
        toUnhideOdds.forEach((id) => next.delete(id));
        return next;
      });
    }
    if (toUnhideMarkets.length > 0) {
      setHiddenMarketIds((prev) => {
        const next = new Set(prev);
        toUnhideMarkets.forEach((id) => next.delete(id));
        return next;
      });
    }

    prevDetailMarketsForLifecycleRef.current = detailMarkets;
  }, [detailMarkets]);

  /* ── Fixture-level eviction: a fixture with zero bettable public markets
     remaining is removed from the list view after the same grace period,
     using fixture.marketState (present on every list-patch fixture) as the
     lock signal. ── */
  const prevFixturesForLifecycleRef = useRef([]);
  useEffect(() => {
    const prevById = new Map(prevFixturesForLifecycleRef.current.map((f) => [fxId(f), f]));
    const mode = modeRef.current;
    const toUnhide = [];

    fixtures.forEach((f) => {
      const id = fxId(f);
      if (id == null) return;
      const schedulerId = `fx:${id}`;
      const locked = isEntityLocked(f.marketState) || f.marketCount === 0;

      if (!locked) {
        fixtureEvictionSchedulerRef.current.cancel(schedulerId);
        if (hiddenFixtureIdsRef.current.has(id)) toUnhide.push(id);
        return;
      }

      const prevF = prevById.get(id);
      const wasLocked = prevF ? (isEntityLocked(prevF.marketState) || prevF.marketCount === 0) : false;
      if (!wasLocked || !fixtureEvictionSchedulerRef.current.isPending(schedulerId)) {
        fixtureEvictionSchedulerRef.current.schedule(schedulerId, remainingHideDelayMs(f.marketState ?? f, mode), () => {
          setHiddenFixtureIds((prev) => new Set(prev).add(id));
        });
      }
    });

    if (toUnhide.length > 0) {
      setHiddenFixtureIds((prev) => {
        const next = new Set(prev);
        toUnhide.forEach((id) => next.delete(id));
        return next;
      });
    }

    prevFixturesForLifecycleRef.current = fixtures;
  }, [fixtures]);

  /* ─────────────────────────────────────────────────────
     HELPERS
  ───────────────────────────────────────────────────── */

  const FLASH_DURATION_MS = 2000;

  const flashOdds = useCallback((oddStateId, direction) => {
    setOddsFlash((prev) => ({ ...prev, [oddStateId]: direction }));
    setTimeout(() => {
      setOddsFlash((prev) => {
        const next = { ...prev };
        delete next[oddStateId];
        return next;
      });
    }, FLASH_DURATION_MS);
  }, []);

  const sendAck = useCallback((room, sequence, emittedAt) => {
    const socket = socketRef.current;
    if (!socket?.connected) return;
    socket.emit('ack:sportsnew:patch', { room, sequence, emittedAt });
  }, []);

  // Compare a fixture's old/new mainOdds (home/draw/away) and flash whichever
  // slots actually changed price. Shared by both list-patch shapes (field-patch
  // and ops-upsert) so the diff logic lives in exactly one place.
  const flashMainOddsDiff = useCallback((oldMainOdds, newMainOdds) => {
    diffMainOdds(oldMainOdds, newMainOdds).forEach(({ oddStateId, direction }) => flashOdds(oddStateId, direction));
  }, [flashOdds]);

  const invalidateFixtureDetail = useCallback(({ fixtureId = null, clearState = true } = {}) => {
    const result = invalidateSportsnewFixtureDetailRequest({
      expectedFixtureId: fixtureId,
      detailAbortControllerRef,
      latestDetailRequestRef,
      detailFixtureIdRef,
      marketChunkRafRef,
      clearClickMark: clearDetailClickMark,
    });
    if (result.invalidated) detailLifecycleGenerationRef.current += 1;
    if (!result.invalidated || !clearState) return result.invalidated;

    setDetailFixture(null);
    setDetailMarkets([]);
    setDetailFixtureId(null);
    setDetailLoading(false);
    fixtureSequenceRef.current = null;
    return true;
  }, []);

  /* ─────────────────────────────────────────────────────
     SOCKET EVENT HANDLERS
  ───────────────────────────────────────────────────── */

  const handleListPatch = useCallback(({ sequence, emittedAt, patch }) => {
    // Reject patches for a room we've since left. Confirmed via captured real
    // payloads that every patch carries its own `mode` (and `sport`). Without
    // this, a patch already in flight for the OLD mode when the user switches
    // tabs (e.g. International → Live → back to International) can arrive
    // just after the new tab's fresh REST fetch and, because list-patch
    // merging never prunes (see applyListPatch), permanently re-insert a
    // fixture that has since expired/started — it silently reappears with no
    // further REST fetch to remove it again.
    //
    // mode alone is not a strong enough identity for a multi-room subscription
    // model: page/league/game title/status changes can produce a brand-new
    // filterHash room under the SAME mode+sport (see the fetchFixtures
    // subscription-swap above), and a socket can be mid-transition between two
    // such rooms. So also reject by sport and filterHash against our own
    // active subscription, and by room if the backend ever adds one — all
    // defensive no-ops today for whichever field a given patch doesn't carry.
    const activeListSub = listSubscriptionRef.current;
    if (patch?.room && activeListSub?.room && patch.room !== activeListSub.room) return;
    if (patch?.mode && patch.mode !== modeRef.current) return;
    if (patch?.scope && activeListSub?.scope && patch.scope !== activeListSub.scope) return;
    if (
      patch?.sport &&
      activeListSub?.sport &&
      activeListSub.sport !== 'all' &&
      patch.sport !== activeListSub.sport
    ) return;
    if (
      patch?.filterHash &&
      activeListSub?.filterHash &&
      patch.filterHash !== activeListSub.filterHash
    ) return;

    // Only mark/measure patches that actually pass identity — a rejected
    // patch is never rendered, so timing it would be meaningless (and would
    // accumulate marks that never get cleared by a matching measure).
    markWsPatchReceived('list', sequence);

    // The REST snapshot is authoritative for THIS page's composition —
    // applyListPatch (below) already refuses to insert a fixtureId the page
    // never showed, so it can never silently grow past the page size. A
    // fixture outside the current page is simply ignored here — no auto
    // refetch of any kind, including on a backend-sent needsRefetch/
    // listDirty flag: /sportsnew/fixtures must be called EXACTLY ONCE on
    // initial page load (or an explicit filter/page change/manual refresh),
    // never automatically in response to WS traffic, however rare.

    // Sequence gap → recovery (with 10s cooldown to prevent loop). Because
    // every branch above returns BEFORE this point for a patch belonging to a
    // room we've left, listSequenceRef is only ever read/written by patches
    // from the currently active room — i.e. it behaves as a per-room sequence
    // without needing a room-keyed map.
    if (
      listSequenceRef.current !== null &&
      sequence !== listSequenceRef.current + 1
    ) {
      const now = Date.now();
      const sub = listSubscriptionRef.current;
      const canRecover =
        !listRecoveryInFlightRef.current &&
        now - listRecoveryLastRef.current > 10000 &&
        sub &&
        SportsnewService;

      if (canRecover) {
        listRecoveryInFlightRef.current = true;
        listRecoveryLastRef.current = now;
        const sinceSeq = listSequenceRef.current;
        // Pass the FULL active filter set, not just mode/sport/page/limit/hash —
        // otherwise a league-, game-title-, or tournament-filtered view can
        // get back a wider recovery snapshot merged into it.
        SportsnewService.recoverLists({
          mode: sub.mode,
          scope: sub.scope,
          sport: sub.sport || undefined,
          league_id: selectedLeagueIdRef.current || undefined,
          game_title_id: sub.sport === 'esports' ? selectedGameTitleIdRef.current || undefined : undefined,
          tournament_id: sub.sport === 'esports' ? selectedTournamentIdRef.current || undefined : undefined,
          page: pageRef.current,
          limit: pageSizeRef.current,
          filter_hash: sub.filterHash,
          since: sinceSeq,
        }).then((res) => {
          const rdata = res?.data;
          const recovered = Array.isArray(rdata) ? rdata : (rdata?.rows ?? rdata?.fixtures ?? []);
          if (recovered.length > 0) {
            const rMap = new Map();
            for (const f of recovered) {
              const id = fxId(f);
              if (id != null && !rMap.has(id)) rMap.set(id, f);
            }
            // recoverLists is paginated (page/limit) and the documented endpoint
            // (docs/sport_socket_guide.md) doesn't list league_id/game_title_id
            // as accepted params — we send them anyway (harmless extra query
            // params if unsupported) so a backend that does honor them narrows
            // the snapshot to the active filter instead of the whole sport.
            // Either way the response is never a guaranteed-complete snapshot,
            // so always merge, never prune — same reasoning as the list:patch
            // handler below. Also update-only, same reasoning as applyListPatch:
            // this recovery's room can be broader than the currently-displayed
            // page, so a fixtureId this page never showed must not be inserted —
            // only fixtures already on the page get refreshed.
            setFixtures((prev) => {
              const merged = new Map(prev.map((f) => [fxId(f), f]));
              rMap.forEach((f, id) => {
                if (!merged.has(id)) return;
                merged.set(id, { ...merged.get(id), ...f });
              });
              return Array.from(merged.values());
            });
          }
          const newHash =
            (rdata && !Array.isArray(rdata) ? rdata.subscription?.filterHash : undefined)
            ?? res?.subscription?.filterHash;
          if (newHash) filterHashRef.current = newHash;
          // Advance sequence to what the recovery returned so we don't re-trigger
          const recoveredSeq = rdata?.sequence ?? rdata?.lastSequence;
          if (recoveredSeq != null) listSequenceRef.current = recoveredSeq;
        }).catch(() => {}).finally(() => {
          listRecoveryInFlightRef.current = false;
        });
      }
    }

    listSequenceRef.current = sequence;
    const operatorVisibility = resolveOperatorVisibilityPatch(patch);
    if (operatorVisibility.action === 'suspend') {
      updateOperatorSuspendedFixtureIds(operatorVisibility.fixtureIds, true);
      if (operatorVisibility.fixtureIds.includes(String(detailFixtureIdRef.current))) {
        invalidateFixtureDetail({ fixtureId: detailFixtureIdRef.current });
      }
    } else if (operatorVisibility.action === 'resume') {
      updateOperatorSuspendedFixtureIds(operatorVisibility.fixtureIds, false);
    }

    // Flash mainOdds price changes on list patches (read stale-safe from ref).
    // See resolveMainOddsUpdatesFromListPatch for exactly which patch shape is
    // authoritative — confirmed against a captured real payload.
    resolveMainOddsUpdatesFromListPatch(patch, fixturesRef.current).forEach(
      ({ oldMainOdds, newMainOdds }) => flashMainOddsDiff(oldMainOdds, newMainOdds)
    );

    // If this list-patch's fixture is the one currently open in the detail
    // panel, ALSO push its fresh mainOdds prices into detailMarkets — see
    // applyMainOddsToDetailMarkets for why this is necessary.
    setDetailMarkets((prev) => applyMainOddsToDetailMarkets(patch, detailFixtureIdRef.current, prev));

    setFixtures((prev) => {
      const next = applyListPatch(prev, stripOperatorVisibilityRemovals(patch));
      return upsertOperatorResumedFixtures(next, operatorVisibility, pageSizeRef.current);
    });
    sendAck(patch?.room || `sportsnew:list`, sequence, emittedAt);
    measureWsPatchToRender('list', sequence);
  // page/pageSize deliberately excluded — read via pageRef/pageSizeRef above so
  // pagination never recreates this callback (see the socket-connection effect,
  // which tears down and rebuilds the whole socket whenever this identity changes).
  }, [SportsnewService, sendAck, flashMainOddsDiff, invalidateFixtureDetail, updateOperatorSuspendedFixtureIds]);

  const handleFixturePatch = useCallback(({ sequence, emittedAt, patch }) => {
    markWsPatchReceived('fixture', sequence);
    const normalizedPatch = normalizeFixtureRoomPatch(patch);
    const activeDetailFixtureId = detailFixtureIdRef.current;
    const activeDetailIdentity = {
      fixtureId: activeDetailFixtureId,
      mode: sportsnewActiveFixtureMode(modeRef.current),
      scope: displayScopeRef.current,
    };
    if (!isSportsnewFixturePatchForActiveDetail(normalizedPatch, activeDetailIdentity)) {
      const staleFixtureId = sportsnewFixturePatchTargetId(normalizedPatch);
      sendAck(normalizedPatch?.room || `sportsnew:fixture:${staleFixtureId ?? 'unknown'}`, sequence, emittedAt);
      measureWsPatchToRender('fixture', sequence);
      return;
    }
    const operatorVisibility = resolveOperatorVisibilityPatch(normalizedPatch);
    if (operatorVisibility.action === 'suspend') {
      const suspendedDetailFixtureId = detailFixtureIdRef.current;
      fixtureSequenceRef.current = sequence;
      updateOperatorSuspendedFixtureIds(operatorVisibility.fixtureIds, true);
      if (operatorVisibility.fixtureIds.includes(String(suspendedDetailFixtureId))) {
        invalidateFixtureDetail({ fixtureId: suspendedDetailFixtureId });
      }
      sendAck(normalizedPatch?.room || `sportsnew:fixture:${suspendedDetailFixtureId}`, sequence, emittedAt);
      measureWsPatchToRender('fixture', sequence);
      return;
    }
    if (operatorVisibility.action === 'resume') {
      updateOperatorSuspendedFixtureIds(operatorVisibility.fixtureIds, false);
    }
    if (
      fixtureSequenceRef.current !== null &&
      sequence !== fixtureSequenceRef.current + 1
    ) {
      const fxId = detailFixtureIdRef.current;
      if (fxId && SportsnewService) {
        const recoveryGeneration = detailLifecycleGenerationRef.current;
        SportsnewService.recoverFixture(fxId, {
          mode: sportsnewActiveFixtureMode(modeRef.current),
          scope: displayScopeRef.current,
          since: fixtureSequenceRef.current,
        }).then((res) => {
          if (!isSportsnewFixtureDetailGenerationCurrent({
            fixtureId: fxId,
            generation: recoveryGeneration,
            detailFixtureIdRef,
            detailLifecycleGenerationRef,
          })) return;
          if (res?.data) {
            const recovered = recoverFixtureSnapshotPayload(res.data);
            if (recovered.fixture) setDetailFixture(recovered.fixture);
            if (Array.isArray(recovered.markets)) {
              setDetailMarkets(reconcileRestDetailMarkets(recovered.markets, recovered.fixture));
            }
          }
        }).catch(() => {});
      }
    }

    fixtureSequenceRef.current = sequence;
    const fixtureMetadataDelta = sportsnewFixtureMetadataDelta(normalizedPatch);
    setDetailFixture((prev) => applyFixturePatchToFixture(prev, fixtureMetadataDelta));
    setFixtures((prev) => upsertOperatorResumedFixtures(
      applyFixturePatchToFixtureList(prev, normalizedPatch, detailFixtureIdRef.current),
      operatorVisibility,
      pageSizeRef.current,
    ));

    // ── Sync detailMarkets from the patch ──────────────────────────────
    // patch.markets[] and patch.odds[] are INDEPENDENT concerns that can BOTH
    // be present, non-empty, in the same payload (confirmed in real captured
    // live traffic) — applyFixtureDetailPatch processes both unconditionally
    // (never if/else-if between them), returning the computed result as pure
    // data. This handler's job is just to dispatch the resulting side effects.
    if (Array.isArray(normalizedPatch?.markets) || Array.isArray(normalizedPatch?.odds) || normalizedPatch?.oddStateId != null) {
      const { markets: nextMarkets, flashEvents, needsRecovery } =
        applyFixtureDetailPatch(detailMarketsRef.current, normalizedPatch);

      flashEvents.forEach(({ oddStateId, direction }) => flashOdds(oddStateId, direction));
      setDetailMarkets(nextMarkets);

      if (needsRecovery && SportsnewService && detailFixtureIdRef.current) {
        const recoveryFixtureId = detailFixtureIdRef.current;
        const recoveryGeneration = detailLifecycleGenerationRef.current;
        SportsnewService.recoverFixture(recoveryFixtureId, { mode: sportsnewActiveFixtureMode(modeRef.current), scope: displayScopeRef.current }).then((res) => {
          if (!isSportsnewFixtureDetailGenerationCurrent({
            fixtureId: recoveryFixtureId,
            generation: recoveryGeneration,
            detailFixtureIdRef,
            detailLifecycleGenerationRef,
          })) return;
          const recovered = recoverFixtureSnapshotPayload(res?.data);
          if (Array.isArray(recovered.markets)) {
            setDetailMarkets(reconcileRestDetailMarkets(recovered.markets, recovered.fixture));
          }
        }).catch(() => {});
      }
    }
    // ops patches: fixture metadata only (score, status, etc.) — detailMarkets
    // was already populated by loadFixtureDetail and stays intact.

    sendAck(normalizedPatch?.room || `sportsnew:fixture:${detailFixtureIdRef.current}`, sequence, emittedAt);
    measureWsPatchToRender('fixture', sequence);
  // mode deliberately excluded — read via modeRef above so switching Live/
  // Prematch tabs never recreates this callback (see the socket-connection
  // effect, which tears down and rebuilds the whole socket whenever this
  // identity changes — this was the cause of the reported "connection keeps
  // dropping" / detail panel barely updating symptom).
  }, [SportsnewService, sendAck, flashOdds, invalidateFixtureDetail, reconcileRestDetailMarkets, updateOperatorSuspendedFixtureIds]);

  const handleSelectedPatch = useCallback(({ sequence, emittedAt, patch }) => {
    markWsPatchReceived('selected', sequence);
    selectedSequenceRef.current = sequence;

    if (patch?.oddStateId != null) {
      selectedWsFreshnessRef.current.set(
        Number(patch.oddStateId),
        selectedPatchFreshness(patch, sequence),
      );
      const cartSel = selectionsRef.current.find((s) => String(s.odds_id) === String(patch.oddStateId));
      const nextOdds = patch.odds ?? patch.price ?? patch.priceDecimal ?? null;
      const hasLockField =
        patch.bettable !== undefined ||
        patch.locked !== undefined ||
        patch.is_suspended !== undefined ||
        patch.suspended !== undefined ||
        patch.state !== undefined ||
        patch.displayState !== undefined;

      // A lock-only patch (bettable/suspended/locked/state with no price at
      // all) must still update the cart/betslip and the pick's disabled/
      // locked label — previously this whole block only ran when a price was
      // present, so a pure lock/unlock patch was silently dropped.
      if (nextOdds != null || hasLockField) {
        if (nextOdds != null) {
          // Prefer the payload's own previousOdds; fall back to the price the
          // cart currently shows for this pick when the backend omits it.
          const prevOdds = patch.previousOdds ?? (cartSel ? Number(cartSel.odds) : null);
          if (prevOdds != null && Number(prevOdds) !== Number(nextOdds)) {
            flashOdds(patch.oddStateId, Number(nextOdds) > Number(prevOdds) ? 'up' : 'down');
          }
        } else if (hasLockField) {
          // No price in this patch — still flash the lock/unlock transition
          // itself, compared against the cart's own last-known lock status,
          // so a lock/suspend is never invisible just because price didn't move.
          const wasLocked = cartSel?.status === 'locked';
          const nowLocked = isOddLocked(patch);
          if (wasLocked !== nowLocked) {
            flashOdds(patch.oddStateId, nowLocked ? 'locked' : 'unlocked');
          }
        }
        // Update the cart's own displayed price/status — this also drives its
        // own flash indicator (FigmaBetCart renders `oddsChanges` independently
        // of the oddsFlash map above, which only covers list/detail cells).
        // newOdds may be null here (lock-only patch) — updateOddsInCart already
        // treats a non-finite newOdds as "no price change", so status/lock
        // fields still apply correctly.
        updateOddsInCart?.(patch.oddStateId, nextOdds, {
          ...(patch.bettable !== undefined && { bettable: patch.bettable }),
          ...(patch.locked !== undefined && { locked: patch.locked }),
          ...(patch.is_suspended !== undefined && { is_suspended: patch.is_suspended }),
        });
        // Also patch the DETAIL PANEL's own markets copy if this odd happens to
        // be shown there. oddsFlash is a global oddStateId-keyed map, so the
        // flash above already lights up the detail-panel cell too (it reads
        // oddsFlash with the same id) — but that cell's PRICE/lock fields come
        // from detailMarkets, which this event never touched before, leaving
        // it stuck on the old values even while it visibly flashed.
        setDetailMarkets((prev) => prev.map((mkt) => {
          const key = mkt.selections ? 'selections' : mkt.odds ? 'odds' : null;
          if (!key) return mkt;
          if (!mkt[key].some((o) => o.oddStateId === patch.oddStateId)) return mkt;
          return {
            ...mkt,
            [key]: mkt[key].map((o) =>
              o.oddStateId === patch.oddStateId
                ? { ...o, ...patch, ...(nextOdds != null && { priceDecimal: nextOdds }) }
                : o
            ),
          };
        }));
      }
    }

    // Update odds in fixture list
    if (patch?.oddStateId != null) {
      setFixtures((prev) => applyOddPatchToFixtureList(prev, patch));
    }

    sendAck(
      `sportsnew:selected:${socketRef.current?.id || ''}`,
      sequence,
      emittedAt
    );
    measureWsPatchToRender('selected', sequence);
  }, [flashOdds, sendAck, updateOddsInCart]);

  const recoverSelectedOddsForCart = useCallback((submittedSelections, requestedScope = displayScope) => {
    return recoverSelectedOddsCoalesced({
      inFlightRequests: selectedRecoveryInFlightRef.current,
      submittedSelections,
      displayScope: requestedScope,
      recoverSelected: SportsnewService?.recoverSelected,
      wsFreshnessById: selectedWsFreshnessRef.current,
      isSelectionActive: isSelectionInAnyCart,
      applyUpdate: ({ oddStateId, priceDecimal, patch }) => {
        updateOddsInCart?.(oddStateId, priceDecimal, patch);
      },
    });
  }, [SportsnewService, displayScope, isSelectionInAnyCart, updateOddsInCart]);

  const handleBetResult = useCallback((data) => {
    // Dispatch a window event so FigmaBetCart / other components can react
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('sportsnew:bet:result', { detail: data }));
    }
  }, []);

  /* ─────────────────────────────────────────────────────
     SOCKET CONNECTION
  ───────────────────────────────────────────────────── */

  useEffect(() => {
    // Vite injects the approved REACT_APP_* client variables via define.
    // eslint-disable-next-line no-undef
    const backUrl = process.env.REACT_APP_BACK_URL;
    if (!backUrl) return undefined;

    const token = session?.user?.token || getStoredAuthToken() || '';

    const socketOptions = {
      withCredentials: true,
      transports: ['websocket', 'polling'],
      // Reconnect indefinitely with capped exponential backoff. A finite
      // reconnectionAttempts (previously 10, exhausted in under a minute)
      // meant any network blip longer than that permanently killed live
      // updates for the rest of the session — the socket gave up retrying
      // and nothing short of a full page reload brought it back, matching
      // reports of flash/detail-panel updates "just stopping" over time.
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 2000,
      reconnectionDelayMax: 10000,
    };

    if (token) {
      socketOptions.auth = { token };
      socketOptions.extraHeaders = { authorization: `Bearer ${token}` };
    }

    const socket = io(`${backUrl}/ws/sports`, socketOptions);

    socket.on('connect', () => {
      setSocketConnected(true);
      // Reset sequences so recovery runs on first patch after reconnect
      listSequenceRef.current = null;
      fixtureSequenceRef.current = null;
      selectedSequenceRef.current = null;

      // Re-subscribe to list room
      const sub = listSubscriptionRef.current;
      if (sub) {
        socket.emit('subscribe:sportsnew:list', sub, (ack) => {
          if (SPORTSNEW_WS_DEBUG) console.log('[sportsnew] list subscribe (on connect) ack:', ack);
          if (!ack?.status) console.warn('[sportsnew] list subscribe failed on connect:', ack);
        });
      }

      // Re-subscribe to fixture room (use refs — this handler is created once
      // when the socket is opened and never recreated on mode/fixture changes,
      // so both must be read live rather than closed over).
      const fxId = detailFixtureIdRef.current;
      if (fxId) {
        socket.emit('subscribe:sportsnew:fixture', {
          fixtureId: fxId,
          mode: sportsnewActiveFixtureMode(modeRef.current),
          scope: displayScopeRef.current,
        });
      }

      // Re-subscribe selected odds
      const oddStateIds = normalizeSelectedRecoveryIds(selectionsRef.current);
      if (oddStateIds.length > 0) {
        socket.emit('subscribe:sportsnew:selected', {
          oddStateIds,
          scope: displayScopeRef.current,
        });
      }
    });

    socket.on('disconnect', () => {
      setSocketConnected(false);
    });

    socket.on('connect_error', (err) => {
      console.warn('[sportsnew] socket connect error:', err.message);
    });

    const rawListPatch = (raw) => {
      if (SPORTSNEW_WS_DEBUG) console.log('[sportsnew:raw] sportsnew:list:patch', raw);
      handleListPatch(raw);
    };
    const rawFixturePatch = (raw) => {
      if (SPORTSNEW_WS_DEBUG) console.log('[sportsnew:raw] sportsnew:fixture:patch', raw);
      handleFixturePatch(raw);
    };
    const rawSelectedPatch = (raw) => {
      if (SPORTSNEW_WS_DEBUG) console.log('[sportsnew:raw] sportsnew:selected:patch', raw);
      handleSelectedPatch(raw);
    };

    socket.on('sportsnew:list:patch', rawListPatch);
    socket.on('sportsnew:fixture:patch', rawFixturePatch);
    socket.on('sportsnew:selected:patch', rawSelectedPatch);
    socket.on('sportsnew:bet:result', handleBetResult);

    socketRef.current = socket;

    return () => {
      socket.off('sportsnew:list:patch', rawListPatch);
      socket.off('sportsnew:fixture:patch', rawFixturePatch);
      socket.off('sportsnew:selected:patch', rawSelectedPatch);
      socket.off('sportsnew:bet:result', handleBetResult);
      socket.disconnect();
      socketRef.current = null;
      setSocketConnected(false);
    };
  // Only reconnect when auth token or handlers change — NOT on detailFixtureId / selections
  // (those are re-subscribed inside the connect handler via refs)
  }, [session?.user?.token, handleListPatch, handleFixturePatch, handleSelectedPatch, handleBetResult]);

  /* ─────────────────────────────────────────────────────
     LIST-ROOM HEARTBEAT (send while list page is active)
  ───────────────────────────────────────────────────── */

  useEffect(() => {
    if (!socketConnected) return undefined;

    heartbeatRef.current = setInterval(() => {
      // A same-tab filter/page change swaps listSubscriptionRef to a new
      // filterHash room without necessarily recreating this effect. Read the
      // ref on every tick so the room that is currently active keeps its TTL.
      const activeSub = listSubscriptionRef.current;
      if (activeSub) {
        socketRef.current?.emit('heartbeat:sportsnew:list', activeSub, () => {});
      }
    }, 25000);

    return () => {
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
      }
    };
  }, [socketConnected, mode, displayScope, selectedSport]);

  /* ─────────────────────────────────────────────────────
     BETSLIP — subscribe selected odds whenever cart changes
  ───────────────────────────────────────────────────── */

  useEffect(() => {
    const oddStateIds = normalizeSelectedRecoveryIds(selections);
    const activeIds = new Set(oddStateIds);
    selectedWsFreshnessRef.current.forEach((value, oddStateId) => {
      if (!activeIds.has(oddStateId)) selectedWsFreshnessRef.current.delete(oddStateId);
    });

    if (!socketConnected) return undefined;

    if (oddStateIds.length === 0) {
      socketRef.current?.emit('unsubscribe:sportsnew:selected', {});
      return undefined;
    }

    socketRef.current?.emit('subscribe:sportsnew:selected', { oddStateIds, scope: displayScope }, (ack) => {
      if (!ack?.status) console.warn('[sportsnew] selected odds subscribe failed');
    });

    return undefined;
  }, [socketConnected, selections, displayScope]);

  /* ─────────────────────────────────────────────────────
     FIXTURE DETAIL — subscribe / unsubscribe on navigate
  ───────────────────────────────────────────────────── */

  useEffect(() => {
    if (!socketConnected || !detailFixtureId) return undefined;
    const fixtureMode = sportsnewActiveFixtureMode(mode);
    socketRef.current?.emit('subscribe:sportsnew:fixture', {
      fixtureId: detailFixtureId,
      mode: fixtureMode,
      scope: displayScope,
    });

    return () => {
      socketRef.current?.emit('unsubscribe:sportsnew:fixture', {
        fixtureId: detailFixtureId,
        mode: fixtureMode,
        scope: displayScope,
      });
      fixtureSequenceRef.current = null;
    };
  }, [socketConnected, detailFixtureId, mode, displayScope]);

  /* ─────────────────────────────────────────────────────
     API — initial load: categories + settings
  ───────────────────────────────────────────────────── */

  useEffect(() => {
    if (!SportsnewService) return;

    Promise.allSettled([
      SportsnewService.getCategories(),
      SportsnewService.getDisplaySettings(),
      SportsnewService.getComboBonus(),
    ]).then(([catRes, settingsRes, bonusRes]) => {
      if (catRes.status === 'fulfilled' && catRes.value?.data) {
        setCategories(normalizeSportsnewCategories(catRes.value.data));
      }
      if (settingsRes.status === 'fulfilled' && settingsRes.value?.data) {
        setDisplaySettings(settingsRes.value.data);
      }
      if (bonusRes.status === 'fulfilled' && bonusRes.value?.data) {
        setComboBonus(normalizeSportsnewComboBonus(bonusRes.value.data));
        if (bonusRes.value?.buckets) setComboBuckets(bonusRes.value.buckets);
      }
    });
  }, [SportsnewService]);

  /* ─────────────────────────────────────────────────────
     API — fetch fixtures when mode / sport / page changes
  ───────────────────────────────────────────────────── */

  const fetchFixtures = useCallback((opts = {}) => {
    if (!SportsnewService) return;

    const params = {
      mode: opts.mode ?? mode,
      scope: opts.scope ?? displayScope,
      page: opts.page ?? page,
      limit: pageSize,
    };

    const sport = opts.sport !== undefined ? opts.sport : selectedSport;
    const sportSlug = sport ? (typeof sport === 'object' ? sport.slug : sport) : null;
    if (sportSlug) params.sport = sportSlug;

    const leagueId = opts.league_id !== undefined ? opts.league_id : selectedLeagueId;
    if (leagueId) params.league_id = leagueId;

    const isEsportsFilter = String(sportSlug || '').toLowerCase() === 'esports' || params.scope === 'esports';

    const gameTitleId = opts.game_title_id !== undefined ? opts.game_title_id : selectedGameTitleId;
    if (isEsportsFilter && gameTitleId) params.game_title_id = gameTitleId;

    const tournamentId = opts.tournament_id !== undefined ? opts.tournament_id : selectedTournamentId;
    if (isEsportsFilter && tournamentId) params.tournament_id = tournamentId;

    if (!isListFilterModeScopeReady(params)) return;

    const requestKey = sportsnewListRequestKey(params);
    const existingFlight = listRequestsInFlightRef.current.get(requestKey);
    if (existingFlight && !existingFlight.cancelled) {
      activeListRequestRef.current = existingFlight;
      return existingFlight.promise;
    }

    const requestFlight = {
      key: requestKey,
      sequence: ++fixturesRequestSeqRef.current,
      cancelled: false,
      retryWait: null,
      abortController: new AbortController(),
      promise: null,
    };
    activeListRequestRef.current = requestFlight;

    const isCurrentRequest = () => (
      !requestFlight.cancelled
      && activeListRequestRef.current === requestFlight
      && subscriptionValue(params.mode) === subscriptionValue(modeRef.current)
      && subscriptionValue(params.scope) === subscriptionValue(displayScopeRef.current)
      && subscriptionValue(params.sport) === sportSlugOf(selectedSportRef.current)
      && subscriptionValue(params.league_id) === subscriptionValue(selectedLeagueIdRef.current)
      && subscriptionValue(params.game_title_id) === subscriptionValue(selectedGameTitleIdRef.current)
      && subscriptionValue(params.tournament_id) === subscriptionValue(selectedTournamentIdRef.current)
      && Number(params.page || 1) === Number(pageRef.current || 1)
      && Number(params.limit || pageSizeRef.current || pageSize) === Number(pageSizeRef.current || pageSize)
    );

    setLoading(true);
    setError(null);
    setListStatus(SPORTSNEW_LIST_STATUS.LOADING);

    const requestPromise = (async () => {
      let retryCount = 0;
      let res;
      let classification;

      try {
        do {
          res = await SportsnewService.getFixtures(params, retryCount > 0 ? {
            cacheBuster: `${Date.now()}-${requestFlight.sequence}`,
            signal: requestFlight.abortController.signal,
          } : { signal: requestFlight.abortController.signal });
          if (!isCurrentRequest()) return;

          classification = classifySportsnewListResponse(res);
          if (!shouldRetrySportsnewList(classification, retryCount)) break;

          retryCount += 1;
          const retryDelayMs = sportsnewListRetryDelayMs(res);
          if (retryDelayMs > 0) {
            const shouldContinue = await new Promise((resolve) => {
              const pending = { resolve, timer: null };
              pending.timer = setTimeout(() => {
                if (requestFlight.retryWait === pending) requestFlight.retryWait = null;
                resolve(true);
              }, retryDelayMs);
              requestFlight.retryWait = pending;
            });
            if (!shouldContinue || !isCurrentRequest()) return;
          }
        } while (retryCount <= 1);

        if (classification?.status === SPORTSNEW_LIST_STATUS.ERROR) {
          const message = classification.reason === 'transition'
            ? '라이브 경기 목록이 갱신 중입니다. 다시 시도해 주세요.'
            : (res?.message || '경기 목록을 불러오지 못했습니다.');
          setError(message);
          setListStatus(SPORTSNEW_LIST_STATUS.ERROR);
          return;
        }

        const data = res?.data;
        const dedupMap = new Map();
        for (const f of classification.rows) {
          const id = fxId(f);
          if (id != null && !dedupMap.has(id)) dedupMap.set(id, f);
        }
        const nextFixtures = Array.from(dedupMap.values());

        setOperatorSuspendedFixtureIds((prev) => {
          const next = new Set([...prev].filter(id => dedupMap.has(id)));
          return next.size === prev.size ? prev : next;
        });
        setFixtures(nextFixtures);
        setListStatus(classification.status);
        markListLoaded();

        const totalItems = data?.total ?? data?.totalCount ?? data?.totalPages ?? res?.total ?? 1;
        const limitVal = data?.limit ?? params.limit ?? pageSize;
        const nextTotalPages = Math.max(1, Math.ceil(Number(totalItems) / Number(limitVal)));
        setTotalPages(nextTotalPages);
        listLastGoodByKeyRef.current.set(requestKey, {
          fixtures: nextFixtures,
          totalPages: nextTotalPages,
        });

        const hash = (data && !Array.isArray(data) ? data.subscription?.filterHash : undefined)
          ?? res?.subscription?.filterHash;

        if (hash) {
          filterHashRef.current = hash;
          const echoed = data?.subscription ?? {};
          const sub = normalizedListSubscription({
            echoed,
            params,
            hash,
            pageFallback: page,
            limitFallback: pageSize,
          });
          const prevSub = listSubscriptionRef.current;
          listSubscriptionRef.current = sub;
          listSequenceRef.current = null;

          if (socketRef.current?.connected && listSubscriptionChanged(prevSub, sub)) {
            if (prevSub) socketRef.current.emit('unsubscribe:sportsnew:list', prevSub);
            socketRef.current.emit('subscribe:sportsnew:list', sub, (ack) => {
              if (!ack?.status) console.warn('[sportsnew] list subscribe failed:', ack);
            });
          }
        } else {
          console.warn('[sportsnew] no filterHash in fixtures response — socket list updates disabled');
        }
      } catch (err) {
        if (!isCurrentRequest()) return;
        console.error('[sportsnew] fetchFixtures error:', err);
        setError(err?.message || '경기 목록을 불러오지 못했습니다.');
        setListStatus(SPORTSNEW_LIST_STATUS.ERROR);
      } finally {
        if (isCurrentRequest()) setLoading(false);
      }
    })();

    requestFlight.promise = requestPromise;
    listRequestsInFlightRef.current.set(requestKey, requestFlight);
    requestPromise.finally(() => {
      const current = listRequestsInFlightRef.current.get(requestKey);
      if (current === requestFlight) listRequestsInFlightRef.current.delete(requestKey);
    });
    return requestPromise;
  }, [SportsnewService, mode, displayScope, page, pageSize, selectedSport, selectedLeagueId, selectedGameTitleId, selectedTournamentId]);

  // Deferred, not immediate. `mode` starts at a hardcoded default ('prematch')
  // regardless of which tab is actually loading — each page's own mount
  // effect (e.g. InplayPage sets mode:'live', DomesticPage/InternationalPage
  // reset selectedSport) corrects it a moment later, in the SAME synchronous
  // effects-flush but as a SEPARATE render pass. Firing fetchFixtures()
  // immediately on the FIRST pass fired one REST call for the stale default,
  // immediately followed by a second call once the page's correction landed
  // — visible as both /sportsnew/fixtures?mode=live and ?mode=prematch firing
  // back to back on a single page load. Deferring to a macrotask lets any
  // synchronous corrections from mounting/unmounting page components settle
  // first (each one recreates fetchFixtures and re-schedules this timer,
  // cancelling the previous one), so only the FINAL settled filter state
  // actually reaches the network.
  useEffect(() => {
    const timer = setTimeout(() => { fetchFixtures(); }, 0);
    return () => clearTimeout(timer);
  }, [fetchFixtures]);

  /* ─────────────────────────────────────────────────────
     API — fetch featured fixtures
  ───────────────────────────────────────────────────── */

  const fetchFeatured = useCallback(async () => {
    if (!SportsnewService) return;
    const res = await SportsnewService.getFeatured({ mode, scope: displayScope }).catch(() => null);
    if (res?.data) {
      setFeaturedFixtures(Array.isArray(res.data) ? res.data : res.data?.fixtures ?? []);
    }
  }, [SportsnewService, mode, displayScope]);

  useEffect(() => {
    fetchFeatured();
  }, [fetchFeatured]);

  /* ─────────────────────────────────────────────────────
     API — fetch leagues when sport changes
  ───────────────────────────────────────────────────── */

  useEffect(() => {
    if (!SportsnewService || !selectedSport) {
      setLeagues([]);
      return;
    }
    const slug = typeof selectedSport === 'object' ? selectedSport.slug : selectedSport;
    SportsnewService.getLeagues(slug)
      .then((res) => {
        if (res?.data) setLeagues(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => setLeagues([]));
  }, [SportsnewService, selectedSport]);

  /* ─────────────────────────────────────────────────────
     API — league -> country lookup (navigation tree)
  ───────────────────────────────────────────────────── */

  useEffect(() => {
    if (!SportsnewService) return;
    // No sport param — confirmed via a captured navigation response that
    // omitting it returns EVERY country/league across all sports in one
    // payload (78 countries / 300 leagues), so this only needs to run once
    // per mode rather than being re-fetched on every sport-chip click.
    SportsnewService.getNavigation({ mode: mode === 'all' ? 'prematch' : mode, scope: displayScope })
      .then((res) => {
        const countries = res?.data?.countries;
        if (!Array.isArray(countries)) return;
        const map = {};
        countries.forEach((country) => {
          const countryName = country?.displayName ?? country?.name ?? '';
          (country?.leagues ?? []).forEach((league) => {
            if (league?.leagueId != null) map[league.leagueId] = countryName;
          });
        });
        if (Object.keys(map).length > 0) {
          setLeagueCountryMap((prev) => ({ ...prev, ...map }));
        }
      })
      .catch(() => {});
  }, [SportsnewService, mode, displayScope]);

  /* ─────────────────────────────────────────────────────
     API — fetch counts
  ───────────────────────────────────────────────────── */

  const fetchCounts = useCallback(async () => {
    if (!SportsnewService) return;
    const [countsRes, liveRes] = await Promise.allSettled([
      SportsnewService.getCounts({ mode, scope: displayScope }),
      SportsnewService.getLiveCount({ scope: 'live' }),
    ]);
    if (countsRes.status === 'fulfilled' && countsRes.value?.data) {
      setCounts(countsRes.value.data);
    }
    if (liveRes.status === 'fulfilled') {
      setLiveCount(
        Number(liveRes.value?.data?.count ?? liveRes.value?.data?.total ?? liveRes.value?.count ?? 0)
      );
    }
  }, [SportsnewService, mode, displayScope]);

  useEffect(() => {
    fetchCounts();
    const interval = setInterval(fetchCounts, 60000);
    return () => clearInterval(interval);
  }, [fetchCounts]);

  /* ─────────────────────────────────────────────────────
     API — fixture detail
  ───────────────────────────────────────────────────── */

  // Progressive/chunked market render: a heavy fixture (soccer/basketball
  // with 100+ markets) is never set into state in one giant call — the
  // first chunk (the first visible market group) renders immediately, and
  // the rest streams in across animation frames so the main thread never
  // blocks on one huge re-render. Cache-hit revalidation bypasses this helper
  // so an already-complete detail never regresses to progressive prefixes.
  const MARKET_CHUNK_SIZE = 6;
  const renderMarketsProgressively = useCallback((fixtureId, markets, measureFromClick) => {
    if (marketChunkRafRef.current) {
      cancelAnimationFrame(marketChunkRafRef.current);
      marketChunkRafRef.current = null;
    }

    if (markets.length <= MARKET_CHUNK_SIZE) {
      setDetailMarkets(markets);
      if (measureFromClick) {
        markPerf(`sportsnew:first_market:${fixtureId}`); measureFirstMarket(fixtureId);
        markPerf(`sportsnew:full_detail:${fixtureId}`); measureFullDetailLoaded(fixtureId);
      }
      return;
    }

    let renderedCount = 0;
    const pushChunk = () => {
      // Superseded by a newer fixture selection while this was scheduled — stop.
      if (latestDetailRequestRef.current !== fixtureId) { marketChunkRafRef.current = null; return; }

      renderedCount += MARKET_CHUNK_SIZE;
      const next = markets.slice(0, renderedCount);
      setDetailMarkets(next);

      if (renderedCount <= MARKET_CHUNK_SIZE && measureFromClick) {
        markPerf(`sportsnew:first_market:${fixtureId}`); measureFirstMarket(fixtureId);
      }

      if (renderedCount < markets.length) {
        marketChunkRafRef.current = requestAnimationFrame(pushChunk);
      } else {
        marketChunkRafRef.current = null;
        if (measureFromClick) {
          markPerf(`sportsnew:full_detail:${fixtureId}`); measureFullDetailLoaded(fixtureId);
        }
      }
    };
    pushChunk();
  }, []);

  const loadFixtureDetail = useCallback(async (fixtureId) => {
    if (!SportsnewService || !fixtureId) return;

    // A newer selection supersedes every part of the previous request lifecycle
    // (HTTP, progressive RAF, stale-response guard, and click perf mark).
    invalidateFixtureDetail({ clearState: false });
    markDetailClick(fixtureId);
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    detailAbortControllerRef.current = controller;

    // Mark as latest request; stale concurrent responses will be discarded below.
    // There is deliberately no time-based cooldown here — clicking back to a
    // recently-viewed match must reload it immediately (setDetailFixtureId
    // below also drives the WS fixture-room resubscription, so skipping this
    // silently left both the panel AND its live updates stuck on the old match).
    latestDetailRequestRef.current = fixtureId;
    detailFixtureIdRef.current = fixtureId;
    const detailGeneration = detailLifecycleGenerationRef.current + 1;
    detailLifecycleGenerationRef.current = detailGeneration;
    setDetailFixtureId(fixtureId);

    const cacheKey = `${fixtureId}:${mode}:${displayScope}`;
    const cached = detailCacheRef.current.get(cacheKey);

    if (cached) {
      // Cache hit — paint the shell AND every market instantly; the network
      // call below still runs as a background revalidate so odds/lock-state
      // never stay stuck on a stale snapshot.
      setDetailFixture(cached.fixture);
      setDetailMarkets(cached.markets);
      setDetailLoading(false);
      markPerf(`sportsnew:header:${fixtureId}`); measureDetailHeader(fixtureId);
      markPerf(`sportsnew:first_market:${fixtureId}`); measureFirstMarket(fixtureId);
      markPerf(`sportsnew:full_detail:${fixtureId}`); measureFullDetailLoaded(fixtureId);
    } else {
      // Cache miss — render the shell/header immediately from whatever the
      // LIST already knows about this fixture (teams/score/league), so
      // there's never a blank panel while markets are still in flight.
      const listFixture = fixturesRef.current.find((f) => fxId(f) === String(fixtureId)) || null;
      setDetailFixture(listFixture);
      setDetailMarkets([]);
      setDetailLoading(true);
      markPerf(`sportsnew:header:${fixtureId}`); measureDetailHeader(fixtureId);
    }

    try {
      // Markets endpoint returns { fixture, markets[] } — no need for a separate fixture call
      const marketsRes = await SportsnewService.getFixtureMarkets(
        fixtureId,
        { mode, scope: displayScope },
        controller ? { signal: controller.signal } : {}
      );

      // Discard if a newer request was issued while this one was in-flight
      if (!isSportsnewFixtureDetailGenerationCurrent({
        fixtureId,
        generation: detailGeneration,
        detailFixtureIdRef,
        detailLifecycleGenerationRef,
      })) return;

      if (marketsRes?.status === false) {
        if ([404, 410].includes(Number(marketsRes?.httpStatus))) {
          detailCacheRef.current.delete(cacheKey);
          invalidateFixtureDetail({ fixtureId });
          return;
        }
        throw new Error(marketsRes?.message || 'SPORTSNEW_DETAIL_MARKETS_FAILED');
      }

      const md = marketsRes?.data;
      // decorate() spreads the response body — fixture/markets may be on marketsRes directly
      const fixture = md?.fixture ?? marketsRes?.fixture ?? null;
      const marketCategories = Array.isArray(md?.categories)
        ? md.categories
        : Array.isArray(marketsRes?.categories)
          ? marketsRes.categories
          : [];
      const fixtureWithMarketCategories = fixture
        ? { ...fixture, marketCategories }
        : null;
      if (fixtureWithMarketCategories) setDetailFixture(fixtureWithMarketCategories);
      const marketList = Array.isArray(md?.markets) ? md.markets
        : Array.isArray(md) ? md
        : Array.isArray(md?.rows) ? md.rows
        : Array.isArray(marketsRes?.markets) ? marketsRes.markets
        : Array.isArray(marketsRes?.rows) ? marketsRes.rows
        : [];
      const dedupedMarkets = reconcileRestDetailMarkets(marketList, fixtureWithMarketCategories ?? fixture ?? md);

      detailCacheRef.current.set(cacheKey, {
        fixture: fixtureWithMarketCategories ?? cached?.fixture ?? null,
        markets: dedupedMarkets,
        cachedAt: Date.now(),
      });

      if (cached) {
        setDetailMarkets(dedupedMarkets);
      } else {
        renderMarketsProgressively(fixtureId, dedupedMarkets, true);
      }
    } catch (err) {
      if (!isSportsnewFixtureDetailGenerationCurrent({
        fixtureId,
        generation: detailGeneration,
        detailFixtureIdRef,
        detailLifecycleGenerationRef,
      })) return;
      if (err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') return;
      clearDetailClickMark(fixtureId);
      console.error('[sportsnew] loadFixtureDetail error:', err);
    } finally {
      if (isSportsnewFixtureDetailGenerationCurrent({
        fixtureId,
        generation: detailGeneration,
        detailFixtureIdRef,
        detailLifecycleGenerationRef,
      })) {
        setDetailLoading(false);
      }
    }
  }, [SportsnewService, mode, displayScope, invalidateFixtureDetail, renderMarketsProgressively, reconcileRestDetailMarkets]);

  const clearFixtureDetail = useCallback(() => {
    invalidateFixtureDetail();
  }, [invalidateFixtureDetail]);

  /* ─────────────────────────────────────────────────────
     Pagination helpers
  ───────────────────────────────────────────────────── */

  const goToPage = useCallback((nextPage) => {
    setPage(Math.max(1, Math.min(nextPage, totalPages)));
  }, [totalPages]);

  /* ─────────────────────────────────────────────────────
     Unsubscribe list room on unmount
  ───────────────────────────────────────────────────── */

  useEffect(() => () => {
    const sub = listSubscriptionRef.current;
    if (socketRef.current?.connected && sub) {
      socketRef.current.emit('unsubscribe:sportsnew:list', sub);
    }
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
  }, []);

  /* ─────────────────────────────────────────────────────
     Visible fixtures — re-apply the league filter client-side.
     The sportsnew:list socket room is keyed by (mode, sport) only — never
     league_id (see docs/sport_socket_guide.md) — so a full-snapshot list
     patch for the room legitimately contains every league in that sport.
     Without this, such a patch silently undoes a league selection made via
     the REST-filtered fetch.
  ───────────────────────────────────────────────────── */

  const visibleFixtures = useMemo(() => {
    let list = fixtures;
    if (selectedLeagueId) {
      list = list.filter((f) => String(f.leagueId ?? f.league?.id ?? f.league_id) === String(selectedLeagueId));
    }
    if (hiddenFixtureIds.size > 0) {
      list = list.filter((f) => !hiddenFixtureIds.has(fxId(f)));
    }
    if (operatorSuspendedFixtureIds.size > 0) {
      list = list.filter((f) => !operatorSuspendedFixtureIds.has(fxId(f)));
    }
    return list;
  }, [fixtures, selectedLeagueId, hiddenFixtureIds, operatorSuspendedFixtureIds]);

  /* ── Visible detail markets — detailMarkets stays the canonical, never-
     purged "soft delete" source; this is the ready-to-render view with
     evicted markets/odds removed. Pages should render THIS, not
     detailMarkets directly, once they adopt the lifecycle. ── */
  const visibleDetailMarkets = useMemo(() => {
    if (hiddenMarketIds.size === 0 && hiddenOddIds.size === 0) return detailMarkets;
    return detailMarkets
      .filter((m) => !hiddenMarketIds.has(m.marketStateId ?? m.id))
      .map((m) => {
        const key = m.selections ? 'selections' : m.odds ? 'odds' : null;
        if (!key || hiddenOddIds.size === 0) return m;
        const filtered = m[key].filter((o) => isDisplayOnlyPairSlot(o) || !hiddenOddIds.has(o.oddStateId));
        return filtered.length === m[key].length ? m : { ...m, [key]: filtered };
      });
  }, [detailMarkets, hiddenMarketIds, hiddenOddIds]);

  /* ─────────────────────────────────────────────────────
     Context value
  ───────────────────────────────────────────────────── */

  const value = useMemo(() => ({
    /* data */
    categories,
    leagues,
    leagueCountryMap,
    // Soft-delete filtered views — the raw, never-purged canonical arrays
    // stay in internal state (fixtures/detailMarkets refs above); everything
    // outside this provider renders through these instead, so eviction
    // (fixture removed / market or selection hidden after its grace period)
    // is transparent to every page without per-page changes.
    fixtures: visibleFixtures,
    featuredFixtures,
    detailFixture,
    detailMarkets: visibleDetailMarkets,
    hiddenOddIds,
    hiddenMarketIds,
    hiddenFixtureIds,
    operatorSuspendedFixtureIds,
    counts,
    liveCount,
    displaySettings,
    comboBonus,
    comboBuckets,
    bettingLimits,
    bettingPolicy,

    /* state */
    mode,
    setMode,
    displayScope,
    setDisplayScope,
    selectedSport,
    setSelectedSport,
    selectedLeagueId,
    setSelectedLeagueId,
    selectedGameTitleId,
    setSelectedGameTitleId,
    selectedTournamentId,
    setSelectedTournamentId,
    loading,
    listStatus,
    detailLoading,
    error,

    /* pagination */
    page,
    totalPages,
    goToPage,

    /* socket */
    socketConnected,
    oddsFlash,

    /* actions */
    fetchFixtures,
    fetchFeatured,
    loadFixtureDetail,
    clearFixtureDetail,
    fetchCounts,
    recoverSelectedOddsForCart,
  }), [
    categories, leagues, leagueCountryMap, visibleFixtures, featuredFixtures, detailFixture, visibleDetailMarkets,
    hiddenOddIds, hiddenMarketIds, hiddenFixtureIds, operatorSuspendedFixtureIds,
    counts, liveCount, displaySettings, comboBonus, comboBuckets, bettingLimits, bettingPolicy,
    mode, displayScope, selectedSport, selectedLeagueId, selectedGameTitleId, selectedTournamentId, loading, listStatus, detailLoading, error,
    page, totalPages, goToPage,
    socketConnected, oddsFlash,
    fetchFixtures, fetchFeatured, loadFixtureDetail, clearFixtureDetail, fetchCounts, recoverSelectedOddsForCart,
  ]);

  return (
    <SportsnewContext.Provider value={value}>
      {children}
    </SportsnewContext.Provider>
  );
};

export default SportsnewContext;
